import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { creatorsAPI } from '../api/creators';
import CreatorStreamHistory from '../components/CreatorStreamHistory';
import CreatorGrowthChart from '../components/CreatorGrowthChart';
import EditCreatorModal from '../components/EditCreatorModal';
import { getCountryFlag } from '../utils/formatters';
import { reachOf } from '../utils/reportMetrics';
import YouTubeEstimateTooltip from '../components/YouTubeEstimateTooltip';
import PlatformIcon from '../components/PlatformIcon';
import { ExternalLink, Plus, Pencil, Archive, ChevronDown, ChevronRight, ArrowLeft, Radio, TrendingUp, TrendingDown, X } from 'lucide-react';

const CARD_BG = 'rgba(255,255,255,0.08)';
const CARD_BORDER = 'rgba(255,255,255,0.14)';
const PLATFORM_COLORS = {
  twitch: '#9146ff',
  kick: '#00e70a',
  youtube: '#ff0000',
  tiktok: '#25F4EE',
  instagram: '#e4405f',
};

const CreatorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statsTimeRange, setStatsTimeRange] = useState(30);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [panelExpandedRows, setPanelExpandedRows] = useState(new Set());
  const [panelContentFilter, setPanelContentFilter] = useState('all');
  const [liveStatus, setLiveStatus] = useState({ isLive: false, avgCcv: 0, peakCcv: 0, viewerCount: 0, watchHours: 0 });
  const [periodStats, setPeriodStats] = useState(null);
  const [periodStatsLoading, setPeriodStatsLoading] = useState(false);
  const periodStatsFetchedRef = useRef(false);
  // 30-day rollup, fetched once after the creator loads. Only populated for
  // Twitch creators with monitored streams in the window; null for everyone
  // else, in which case the tiles aren't rendered.
  const [rollup, setRollup] = useState(null);

  const fetchCreator = async () => {
    try {
      setLoading(true);
      const response = await creatorsAPI.getOne(id);
      const creatorData = response.data.creator || response.data;
      if (creatorData.Content && !creatorData.content) creatorData.content = creatorData.Content;
      // Normalize PascalCase relations to camelCase
      if (creatorData.content) {
        creatorData.content = creatorData.content.map(item => ({
          ...item,
          segments: item.segments || item.ContentSegment?.map(seg => ({ ...seg, game: seg.game || seg.Game })) || [],
          game: item.game || item.Game || null,
        }));
      }
      setCreator(creatorData);
      if (creatorData.liveStatus) setLiveStatus(creatorData.liveStatus);
      const primaryPlatform = creatorData.platform || (creatorData.content?.[0]?.platform || 'twitch');
      if (primaryPlatform === 'twitch') {
        fetchPeriodStats(creatorData.id);
        fetchRollup(creatorData.id);
      }
    } catch (err) {
      setError('Failed to fetch creator details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { periodStatsFetchedRef.current = false; setPeriodStats(null); fetchCreator(); }, [id]);

  useEffect(() => {
    if (!liveStatus.isLive) return;
    const interval = setInterval(async () => {
      try {
        // Poll the small live-status endpoint, not the full creator payload
        // (which carries the entire content history every 30 s).
        const response = await creatorsAPI.getLiveStatus(id);
        if (response.data?.liveStatus) setLiveStatus(response.data.liveStatus);
      } catch (err) {}
    }, 30000);
    return () => clearInterval(interval);
  }, [id, liveStatus.isLive]);

  const fetchPeriodStats = async (creatorId) => {
    if (periodStatsFetchedRef.current || periodStatsLoading) return;
    try {
      setPeriodStatsLoading(true);
      periodStatsFetchedRef.current = true;
      const response = await creatorsAPI.getPeriodStats(creatorId);
      if (response.data?.periodStats) setPeriodStats(response.data.periodStats);
    } catch (err) {} finally { setPeriodStatsLoading(false); }
  };

  // Pull the 30-day rollup. 404 just means we have no monitored streams for
  // this creator in the window — fail silently and the tiles render nothing.
  const fetchRollup = async (creatorId) => {
    try {
      const response = await creatorsAPI.getRollup(creatorId, 30);
      setRollup(response.data);
    } catch (err) {
      setRollup(null);
    }
  };

  const fmt = (n) => n ? n.toLocaleString() : '0';

  // Deduplicate content
  const deduped = useMemo(() => {
    if (!creator?.content) return [];
    const contentMap = new Map();
    creator.content.forEach(item => {
      const key = `${item.title}|${item.platform}|${item.postDate}`;
      const existing = contentMap.get(key);
      if (existing) {
        if (!existing.streamDuration && item.streamDuration) contentMap.set(key, item);
        else if ((item.segments?.length || 0) > (existing.segments?.length || 0)) contentMap.set(key, item);
      } else contentMap.set(key, item);
    });
    return Array.from(contentMap.values());
  }, [creator]);

  // Get which platforms this creator has content for
  const activePlatforms = useMemo(() => {
    if (!creator) return [];
    const p = new Set();
    if (creator.twitchUrl || creator.platform === 'twitch') p.add('twitch');
    if (creator.kickUrl || creator.kickId || creator.platform === 'kick') p.add('kick');
    if (creator.youtubeUrl || creator.platform === 'youtube') p.add('youtube');
    if (creator.tiktokUrl || creator.platform === 'tiktok') p.add('tiktok');
    return Array.from(p);
  }, [creator]);

  // Compute stats per platform for the selected time range
  const platformStats = useMemo(() => {
    const cutoff = new Date(Date.now() - statsTimeRange * 86400000);
    const prevCutoff = new Date(Date.now() - statsTimeRange * 2 * 86400000);
    const result = {};

    activePlatforms.forEach(plat => {
      const items = deduped.filter(c => c.platform?.toLowerCase() === plat && c.postDate && new Date(c.postDate) >= cutoff);
      const prevItems = deduped.filter(c => c.platform?.toLowerCase() === plat && c.postDate && new Date(c.postDate) >= prevCutoff && new Date(c.postDate) < cutoff);

      if (plat === 'twitch' || plat === 'kick') {
        const ccvs = items.map(c => c.streamAvgCcv || 0).filter(v => v > 0);
        const peaks = items.map(c => c.streamPeakCcv || 0).filter(v => v > 0);
        const wh = items.map(c => c.streamWatchHours || 0).filter(v => v > 0);
        const prevCcvs = prevItems.map(c => c.streamAvgCcv || 0).filter(v => v > 0);
        const prevWh = prevItems.map(c => c.streamWatchHours || 0).filter(v => v > 0);

        // Server-side period stats are Twitch-only; Kick falls back to the content rows.
        const ps = plat === 'twitch' ? periodStats?.[String(statsTimeRange)] : null;
        result[plat] = {
          streams: items.length,
          avgCcv: ps?.avgCcv ?? (ccvs.length ? Math.round(ccvs.reduce((a, b) => a + b, 0) / ccvs.length) : 0),
          peakCcv: ps?.peakCcv ?? (peaks.length ? Math.max(...peaks) : 0),
          watchHours: ps?.watchHours ?? (wh.length ? Math.round(wh.reduce((a, b) => a + b, 0)) : 0),
          hoursStreamed: ps?.hoursStreamed ?? items.length,
          prevAvgCcv: prevCcvs.length ? Math.round(prevCcvs.reduce((a, b) => a + b, 0) / prevCcvs.length) : 0,
          prevWatchHours: prevWh.length ? Math.round(prevWh.reduce((a, b) => a + b, 0)) : 0,
        };
      } else {
        const views = items.map(c => reachOf(c)).filter(v => v > 0);
        const totalViews = views.reduce((a, b) => a + b, 0);
        const engRates = items.map(c => c.engagementRate || 0).filter(v => v > 0);
        const prevViews = prevItems.map(c => reachOf(c)).filter(v => v > 0);
        const prevTotal = prevViews.reduce((a, b) => a + b, 0);

        result[plat] = {
          videos: items.length,
          avgViews: views.length ? Math.round(totalViews / views.length) : 0,
          totalViews,
          avgEngagement: engRates.length ? (engRates.reduce((a, b) => a + b, 0) / engRates.length).toFixed(2) : 0,
          totalLikes: items.reduce((s, c) => s + (c.likes || 0), 0),
          totalComments: items.reduce((s, c) => s + (c.comments || 0), 0),
          prevTotalViews: prevTotal,
          prevVideos: prevItems.length,
        };
      }
    });
    return result;
  }, [deduped, activePlatforms, statsTimeRange, periodStats]);

  // Build chart data - all platforms overlaid
  const chartData = useMemo(() => {
    const cutoff = new Date(Date.now() - statsTimeRange * 86400000);
    const now = new Date();
    const byDate = new Map();

    deduped.filter(c => c.postDate && new Date(c.postDate) >= cutoff).forEach(item => {
      const dateKey = new Date(item.postDate).toISOString().split('T')[0];
      if (!byDate.has(dateKey)) byDate.set(dateKey, {});
      const day = byDate.get(dateKey);
      const plat = item.platform?.toLowerCase();
      if (!day[plat]) day[plat] = [];
      day[plat].push(item);
    });

    const result = [];
    for (let i = statsTimeRange - 1; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 86400000);
      const dateKey = d.toISOString().split('T')[0];
      const dayData = byDate.get(dateKey) || {};
      const point = { date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };

      if (dayData.twitch?.length) {
        const ccvs = dayData.twitch.map(c => c.streamAvgCcv || 0).filter(v => v > 0);
        point.twitch = ccvs.length ? Math.round(ccvs.reduce((a, b) => a + b, 0) / ccvs.length) : null;
      }
      if (dayData.kick?.length) {
        const ccvs = dayData.kick.map(c => c.streamAvgCcv || 0).filter(v => v > 0);
        point.kick = ccvs.length ? Math.round(ccvs.reduce((a, b) => a + b, 0) / ccvs.length) : null;
      }
      if (dayData.youtube?.length) {
        const views = dayData.youtube.map(c => reachOf(c)).filter(v => v > 0);
        point.youtube = views.length ? views.reduce((a, b) => a + b, 0) : null;
      }
      if (dayData.tiktok?.length) {
        const views = dayData.tiktok.map(c => reachOf(c)).filter(v => v > 0);
        point.tiktok = views.length ? views.reduce((a, b) => a + b, 0) : null;
      }
      result.push(point);
    }
    return result;
  }, [deduped, statsTimeRange]);

  const hasChartData = chartData.some(d => d.twitch || d.kick || d.youtube || d.tiktok);

  const handleArchiveCreator = async () => {
    setArchiving(true);
    try { await creatorsAPI.archive(id); navigate('/dashboard/creators'); }
    catch (err) { setError('Failed to archive creator.'); setShowArchiveConfirm(false); }
    finally { setArchiving(false); }
  };

  const getDelta = (current, previous) => {
    if (!previous || previous === 0) return null;
    const pct = ((current - previous) / previous * 100).toFixed(0);
    return { value: pct, positive: Number(pct) >= 0 };
  };

  const getPlatformLinks = () => {
    if (!creator) return [];
    const links = [];
    const pa = creator.platformAudience || {};
    if (creator.youtubeUrl) links.push({ platform: 'youtube', label: 'YouTube', url: creator.youtubeUrl, audience: pa.youtubeSubscribers, audienceLabel: 'subscribers', color: '#ff0000' });
    if (creator.twitchUrl || creator.platform === 'twitch') links.push({ platform: 'twitch', label: 'Twitch', url: creator.twitchUrl || `https://twitch.tv/${creator.handle}`, audience: pa.twitchFollowers, audienceLabel: 'followers', color: '#9146ff' });
    if (creator.kickUrl || creator.kickSlug) links.push({ platform: 'kick', label: 'Kick', url: creator.kickUrl || `https://kick.com/${creator.kickSlug}`, audience: pa.kickSubscribers, audienceLabel: 'subscribers', color: '#00e70a' });
    if (creator.tiktokUrl) links.push({ platform: 'tiktok', label: 'TikTok', url: creator.tiktokUrl, audience: pa.tiktokFollowers, audienceLabel: 'followers', color: '#25F4EE' });
    if (creator.instagramUrl) links.push({ platform: 'instagram', label: 'Instagram', url: creator.instagramUrl, audience: pa.instagramFollowers, audienceLabel: 'followers', color: '#e4405f' });
    if (creator.twitterUrl) links.push({ platform: 'twitter', label: 'X', url: creator.twitterUrl, audience: pa.twitterFollowers, audienceLabel: 'followers', color: '#ffffff' });
    if (creator.discordUrl) links.push({ platform: 'discord', label: 'Discord', url: creator.discordUrl, color: '#5865f2' });
    return links;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: 'rgba(20,15,35,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
        <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0 0 4px' }}>{label}</p>
        {payload.filter(e => e.value).map((entry, i) => (
          <p key={i} style={{ color: entry.color, margin: '2px 0', fontWeight: 600 }}>
            {entry.name}: {entry.value?.toLocaleString()}
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-sc-pink/30 border-t-sc-pink rounded-full animate-spin mx-auto" />
          <p className="mt-3 text-white text-sm">Loading creator...</p>
        </div>
      </div>
    );
  }

  if (error || !creator) {
    return (
      <div className="p-6">
        <div className="bg-red-500/15 border border-red-500/25 text-red-400 px-4 py-3 rounded-lg mb-4">{error || 'Creator not found'}</div>
        <button onClick={() => navigate('/dashboard/creators')} className="btn-pink">Back to Creators</button>
      </div>
    );
  }

  const platformLinks = getPlatformLinks();
  const totalReach = platformLinks.reduce((s, p) => s + (p.audience || 0), 0);

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };

  const StatCard = ({ label, value, delta, tooltip, color }) => (
    <div className="rounded-lg p-3.5" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderLeft: `3px solid ${color || '#ec4899'}` }}>
      <div className="flex items-center gap-1.5 mb-1">
        <p className="text-white text-[11px] font-medium">{label}</p>
        {tooltip && <YouTubeEstimateTooltip metric={tooltip} />}
      </div>
      <div className="flex items-end gap-2">
        <p className="text-xl font-bold text-white">{value}</p>
        {delta && (
          <span className={`flex items-center gap-0.5 text-[11px] font-semibold mb-0.5 ${delta.positive ? 'text-emerald-400' : 'text-red-400'}`}>
            {delta.positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(delta.value)}%
          </span>
        )}
      </div>
    </div>
  );

  const PlatformSection = ({ platform, color, label }) => {
    const stats = platformStats[platform];
    if (!stats) return null;
    const isTw = platform === 'twitch' || platform === 'kick';

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-2">
          <PlatformIcon platform={platform} size="sm" />
          <h3 className="text-sm font-semibold text-white">{label}</h3>
          <span className="text-xs text-white">{isTw ? `${stats.streams} streams` : `${stats.videos} videos`}</span>
          <button
            onClick={() => { setExpandedPlatform(expandedPlatform === platform ? null : platform); setPanelContentFilter('all'); }}
            className="ml-auto flex items-center gap-1 text-xs font-medium transition-colors"
            style={{ color: expandedPlatform === platform ? color : 'rgba(255,255,255,0.8)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = color}
            onMouseLeave={(e) => { if (expandedPlatform !== platform) e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
          >
            {expandedPlatform === platform ? 'Close' : 'See more'} <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expandedPlatform === platform ? 'rotate-90' : ''}`} />
          </button>
        </div>
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
          {isTw ? (
            <>
              <StatCard label="Hours Streamed" value={periodStatsLoading ? '...' : fmt(stats.hoursStreamed)} color={color} />
              <StatCard label="Avg CCV" value={periodStatsLoading ? '...' : fmt(stats.avgCcv)} delta={getDelta(stats.avgCcv, stats.prevAvgCcv)} color={color} />
              <StatCard label="Peak CCV" value={periodStatsLoading ? '...' : fmt(stats.peakCcv)} color={color} />
              <StatCard label="Watch Hours" value={periodStatsLoading ? '...' : fmt(stats.watchHours)} delta={getDelta(stats.watchHours, stats.prevWatchHours)} tooltip="watchHours" color={color} />
            </>
          ) : (
            <>
              <StatCard label="Videos" value={fmt(stats.videos)} delta={getDelta(stats.videos, stats.prevVideos)} color={color} />
              <StatCard label="Avg Views" value={fmt(stats.avgViews)} color={color} />
              <StatCard label="Total Views" value={fmt(stats.totalViews)} delta={getDelta(stats.totalViews, stats.prevTotalViews)} color={color} />
              <StatCard label="Engagement" value={`${stats.avgEngagement}%`} tooltip="engagement" color={color} />
            </>
          )}
        </div>
        {platform === 'youtube' && (
          <p className="text-[10px] text-white mt-1">YouTube data via YouTube Data API. Refreshed every 30 days.</p>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {/* Back nav */}
      <button onClick={() => navigate('/dashboard/creators')} className="flex items-center gap-1.5 text-white hover:text-white text-sm transition-colors group">
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        <span>Creators</span>
      </button>

      {/* ===== HERO BANNER ===== */}
      <div className="relative rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.12) 0%, rgba(30,20,50,0.95) 40%, rgba(20,15,35,1) 100%)' }}>
        {liveStatus.isLive && <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500 via-red-400 to-red-500 animate-pulse" />}
        <div className="relative px-6 py-5 flex items-center gap-6">
          <div className="relative flex-shrink-0">
            {creator.profileImageUrl ? (
              <img src={creator.profileImageUrl} alt={creator.name} className={`w-20 h-20 rounded-2xl object-cover ${liveStatus.isLive ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-[#1a0f2e]' : 'ring-1 ring-white/10'}`} />
            ) : (
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br from-sc-pink/30 to-purple-600/30 flex items-center justify-center text-white text-2xl font-bold ${liveStatus.isLive ? 'ring-2 ring-red-500' : 'ring-1 ring-white/10'}`}>
                {creator.name?.charAt(0)?.toUpperCase()}
              </div>
            )}
            {liveStatus.isLive && (
              <span className="absolute -bottom-1 -right-1 flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md shadow-lg shadow-red-500/30">
                <Radio className="w-2.5 h-2.5 animate-pulse" /> LIVE
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-white truncate">{creator.name}</h1>
              {creator.country && <span className="text-sm text-white">{getCountryFlag(creator.country)}</span>}
            </div>
            <p className="text-white text-sm mb-1">@{creator.handle}</p>
            {/* Platform icons row */}
            <div className="flex items-center gap-3 mt-1">
              {platformLinks.map(p => (
                <a key={p.platform} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-white hover:text-white transition-colors" title={p.label}>
                  <PlatformIcon platform={p.platform} size="xs" />
                  {p.audience > 0 && <span className="text-xs">{fmt(p.audience)}</span>}
                </a>
              ))}
            </div>
            {/* 30-day rollup tiles. Renders only when we have monitored-stream
                data for this creator (Twitch) in the window. */}
            {rollup && (
              <div className="flex items-center gap-4 mt-2 text-xs">
                <span className="text-white uppercase tracking-wide">Last 30d</span>
                <span className="text-white">
                  <span className="text-white">Avg CCV</span>{' '}
                  <span className="font-semibold text-white">{fmt(rollup.avgCcv)}</span>
                </span>
                <span className="text-white">·</span>
                <span className="text-white">
                  <span className="text-white">Peak</span>{' '}
                  <span className="font-semibold text-white">{fmt(rollup.peakCcv)}</span>
                </span>
                <span className="text-white">·</span>
                <span className="text-white">
                  <span className="text-white">Watch hrs</span>{' '}
                  <span className="font-semibold text-white">{fmt(rollup.watchHours)}</span>
                </span>
                {rollup.gamesPlayed?.length > 0 && (
                  <>
                    <span className="text-white">·</span>
                    <span className="text-white truncate max-w-[28rem]" title={rollup.gamesPlayed.join(', ')}>
                      <span className="text-white">Top games</span>{' '}
                      <span className="text-white">{rollup.gamesPlayed.slice(0, 3).join(', ')}</span>
                      {rollup.gamesPlayed.length > 3 && (
                        <span className="text-white"> +{rollup.gamesPlayed.length - 3}</span>
                      )}
                    </span>
                  </>
                )}
              </div>
            )}
            {liveStatus.isLive && (
              <div className="flex items-center gap-3 text-sm mt-2">
                <span className="text-white">
                  {liveStatus.allPlatforms?.length > 1
                    ? `Live on ${liveStatus.allPlatforms.map(p => ({ youtube: 'YouTube', kick: 'Kick' }[p.platform] || 'Twitch')).join(' & ')} · ${fmt(liveStatus.allPlatforms.reduce((s, p) => s + (p.viewerCount || 0), 0))} viewers`
                    : `Live on ${{ youtube: 'YouTube', kick: 'Kick' }[liveStatus.platform] || 'Twitch'} · ${fmt(liveStatus.viewerCount || liveStatus.avgCcv)} viewers`}
                </span>
                <a href={liveStatus.streamUrl || (liveStatus.platform === 'kick' ? creator.kickUrl : null) || (creator.twitchUrl || creator.youtubeUrl)} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300 font-medium transition-colors">Watch →</a>
              </div>
            )}
          </div>
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <button className="btn-pink px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 shadow-lg shadow-sc-pink/20">
              <Plus className="w-4 h-4" /> Add to Campaign
            </button>
            <button onClick={() => setShowEditModal(true)} className="p-2 rounded-lg bg-white/5 text-white hover:text-white hover:bg-white/10 border border-white transition-colors" title="Edit">
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={() => setShowArchiveConfirm(true)} className="p-2 rounded-lg bg-white/5 text-orange-400/60 hover:text-orange-400 hover:bg-orange-500/10 border border-white transition-colors" title="Archive">
              <Archive className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ===== TIME RANGE SELECTOR ===== */}
      <div className="flex items-center gap-3">
        <div className="flex rounded-lg overflow-hidden border border-white/8">
          {[3, 7, 30, 60, 90].map(d => (
            <button key={d} onClick={() => setStatsTimeRange(d)} className={`px-4 py-2 text-xs font-semibold transition-all ${statsTimeRange === d ? 'bg-sc-pink text-white' : 'bg-white/[0.03] text-white hover:text-white hover:bg-white/[0.06]'}`}>
              {d}d
            </button>
          ))}
        </div>
        <span className="text-xs text-white">All platforms · Last {statsTimeRange} days</span>
      </div>

      {/* ===== TWO COLUMN LAYOUT ===== */}
      <div className="flex gap-5">

        {/* ============ LEFT SIDEBAR ============ */}
        <div className="w-60 xl:w-64 shrink-0 hidden lg:block">
          <div className="sticky top-6 space-y-3">
            {/* Platform Reach */}
            <div className="rounded-xl p-4" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[11px] font-semibold text-white uppercase tracking-wider">Platform Reach</h3>
                {totalReach > 0 && <span className="text-[11px] text-white">{fmt(totalReach)}</span>}
              </div>
              <div className="space-y-1">
                {platformLinks.map((p) => (
                  <a key={p.platform} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-white/[0.05] transition-colors group">
                    <PlatformIcon platform={p.platform} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{p.label}</p>
                      {p.audience > 0 && <p className="text-[11px] text-white">{fmt(p.audience)} {p.audienceLabel}</p>}
                    </div>
                    <ExternalLink className="w-3 h-3 text-white group-hover:text-white transition-colors" />
                  </a>
                ))}
              </div>
              {platformLinks.some(p => p.platform === 'youtube' && p.audience > 0) && (
                <p className="text-[10px] text-white mt-2">YouTube data via YouTube Data API</p>
              )}
            </div>

            {/* Genres & Tags */}
            {(creator.topGenres?.length > 0 || creator.niche?.length > 0) && (
              <div className="rounded-xl p-4" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
                {creator.topGenres?.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-2">Genres</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {creator.topGenres.map((genre, i) => (
                        <span key={i} className="px-2 py-0.5 text-[11px] text-white rounded bg-sc-pink/10 border border-sc-pink/15">{genre}</span>
                      ))}
                    </div>
                  </div>
                )}
                {creator.niche?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {creator.niche.map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 text-[11px] text-white rounded bg-white/5">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* About */}
            {(creator.description || creator.email) && (
              <div className="rounded-xl p-4" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
                <button onClick={() => setShowAbout(!showAbout)} className="flex items-center justify-between w-full text-[11px] font-semibold text-white uppercase tracking-wider hover:text-white transition-colors">
                  About <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAbout ? 'rotate-180' : ''}`} />
                </button>
                {showAbout && (
                  <div className="mt-3 space-y-2">
                    {creator.description && <p className="text-xs text-white leading-relaxed">{creator.description}</p>}
                    {creator.email && <p className="text-xs text-white">{creator.email}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ============ MAIN CONTENT ============ */}
        <div className="flex-1 min-w-0 space-y-5">

          {/* Per-platform stat sections */}
          {activePlatforms.includes('twitch') && <PlatformSection platform="twitch" color={PLATFORM_COLORS.twitch} label="Twitch" />}
          {activePlatforms.includes('kick') && <PlatformSection platform="kick" color={PLATFORM_COLORS.kick} label="Kick" />}
          {activePlatforms.includes('youtube') && <PlatformSection platform="youtube" color={PLATFORM_COLORS.youtube} label="YouTube" />}
          {activePlatforms.includes('tiktok') && <PlatformSection platform="tiktok" color={PLATFORM_COLORS.tiktok} label="TikTok" />}

          {/* Trend Chart - all platforms */}
          {hasChartData && (
            <div className="rounded-xl p-4" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white">Activity over time</h3>
                <span className="text-[11px] text-white">Last {statsTimeRange} days</span>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradTwitch" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#9146ff" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#9146ff" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradKick" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00e70a" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#00e70a" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradYoutube" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ff0000" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#ff0000" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradTiktok" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#25F4EE" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#25F4EE" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} interval={Math.max(0, Math.floor(chartData.length / 7) - 1)} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip content={<CustomTooltip />} />
                  {activePlatforms.includes('twitch') && (
                    <Area type="monotone" dataKey="twitch" stroke="#9146ff" strokeWidth={2} fill="url(#gradTwitch)" dot={false} connectNulls={false} name="Twitch (Avg CCV)" />
                  )}
                  {activePlatforms.includes('kick') && (
                    <Area type="monotone" dataKey="kick" stroke="#00e70a" strokeWidth={2} fill="url(#gradKick)" dot={false} connectNulls={false} name="Kick (Avg CCV)" />
                  )}
                  {activePlatforms.includes('youtube') && (
                    <Area type="monotone" dataKey="youtube" stroke="#ff0000" strokeWidth={2} fill="url(#gradYoutube)" dot={false} connectNulls={false} name="YouTube (Views)" />
                  )}
                  {activePlatforms.includes('tiktok') && (
                    <Area type="monotone" dataKey="tiktok" stroke="#25F4EE" strokeWidth={2} fill="url(#gradTiktok)" dot={false} connectNulls={false} name="TikTok (Views)" />
                  )}
                </AreaChart>
              </ResponsiveContainer>
              {activePlatforms.length > 1 && (
                <div className="flex items-center gap-4 mt-2 pl-4">
                  {activePlatforms.includes('twitch') && <span className="flex items-center gap-1.5 text-[11px] text-white"><span className="w-2.5 h-0.5 rounded bg-[#9146ff]" />Twitch CCV</span>}
                  {activePlatforms.includes('kick') && <span className="flex items-center gap-1.5 text-[11px] text-white"><span className="w-2.5 h-0.5 rounded bg-[#00e70a]" />Kick CCV</span>}
                  {activePlatforms.includes('youtube') && <span className="flex items-center gap-1.5 text-[11px] text-white"><span className="w-2.5 h-0.5 rounded bg-[#ff0000]" />YouTube Views</span>}
                  {activePlatforms.includes('tiktok') && <span className="flex items-center gap-1.5 text-[11px] text-white"><span className="w-2.5 h-0.5 rounded bg-[#25F4EE]" />TikTok Views</span>}
                </div>
              )}
            </div>
          )}

          {/* Audience growth (daily follower/subscriber snapshots) */}
          <CreatorGrowthChart creatorId={id} />

          {/* All Content Table */}
          <CreatorStreamHistory creatorId={id} selectedPlatform={null} selectedContentType="all" showAllPlatforms={true} activePlatforms={activePlatforms} />
        </div>
      </div>

      {/* Mobile fallback */}
      <div className="lg:hidden space-y-4">
        <div className="rounded-xl p-4" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
          <h3 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-3">Platform Reach</h3>
          <div className="grid grid-cols-2 gap-2">
            {platformLinks.filter(p => p.audience > 0).map(p => (
              <a key={p.platform} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white/[0.03]">
                <PlatformIcon platform={p.platform} size="sm" />
                <div>
                  <p className="text-sm font-medium text-white">{fmt(p.audience)}</p>
                  <p className="text-[10px] text-white">{p.label} {p.audienceLabel}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="flex-1 btn-pink py-2.5 rounded-lg font-medium text-sm">Add to Campaign</button>
          <button onClick={() => setShowEditModal(true)} className="bg-white/5 text-white px-4 py-2.5 rounded-lg text-sm border border-white">Edit</button>
          <button onClick={() => setShowArchiveConfirm(true)} className="bg-orange-500/10 text-orange-400/70 px-4 py-2.5 rounded-lg text-sm border border-orange-500/15">Archive</button>
        </div>
      </div>

      {/* Archive Confirm Modal */}
      {showArchiveConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="rounded-xl p-6 max-w-md w-full mx-4 border border-white" style={{ background: 'rgba(30,24,46,1)' }}>
            <h3 className="text-xl font-bold text-white mb-4">Archive Creator?</h3>
            <p className="text-white mb-6">Archive <strong className="text-white">{creator.name}</strong>? They'll be hidden from the main list and no longer scanned. All data is preserved.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowArchiveConfirm(false)} className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/15 border border-white" disabled={archiving}>Cancel</button>
              <button onClick={handleArchiveCreator} disabled={archiving} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">{archiving ? 'Archiving...' : 'Archive'}</button>
            </div>
          </div>
        </div>
      )}

      <EditCreatorModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} onSuccess={fetchCreator} creator={creator} />

      {/* ===== FLOATING "SEE MORE" OVERLAY (LEFT SIDE) ===== */}
      {expandedPlatform && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setExpandedPlatform(null)} />
          <div className="fixed top-0 left-0 bottom-0 z-50 flex flex-col" style={{ width: 580, background: 'rgba(22,17,36,0.98)', borderRight: `2px solid ${PLATFORM_COLORS[expandedPlatform]}`, boxShadow: '8px 0 40px rgba(0,0,0,0.5)', backdropFilter: 'blur(20px)' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <PlatformIcon platform={expandedPlatform} size="sm" />
                <h3 className="text-sm font-bold text-white">
                  {expandedPlatform === 'twitch' ? 'Twitch' : expandedPlatform === 'youtube' ? 'YouTube' : 'TikTok'}
                </h3>
                <span className="text-xs text-white">· {statsTimeRange}d</span>
              </div>
              <button onClick={() => setExpandedPlatform(null)} className="p-1.5 rounded-lg hover:bg-white/10 text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content type filter tabs for YouTube */}
            {expandedPlatform === 'youtube' && (
              <div className="flex px-4 pt-2 pb-1 gap-1">
                {['all', 'videos', 'streams'].map(f => (
                  <button key={f} onClick={() => setPanelContentFilter(f)} style={{ padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.15s', background: panelContentFilter === f ? '#ec4899' : 'rgba(255,255,255,0.06)', color: panelContentFilter === f ? 'white' : 'rgba(255,255,255,0.6)' }}>
                    {f === 'all' ? 'All' : f === 'videos' ? 'Videos' : 'YouTube Streams'}
                  </button>
                ))}
              </div>
            )}

            {/* Table */}
            <div className="flex-1 overflow-y-auto">
              {(() => {
                let items = deduped
                  .filter(c => c.platform?.toLowerCase() === expandedPlatform)
                  .filter(c => { const cutoff = new Date(Date.now() - statsTimeRange * 86400000); return c.postDate && new Date(c.postDate) >= cutoff; });

                // Apply content type filter for YouTube
                if (expandedPlatform === 'youtube' && panelContentFilter !== 'all') {
                  if (panelContentFilter === 'streams') items = items.filter(c => c.contentType === 'stream' || c.contentType === 'vod');
                  else items = items.filter(c => c.contentType === 'video' || c.contentType === 'short' || !c.contentType);
                }

                items = items.sort((a, b) => new Date(b.postDate) - new Date(a.postDate));
                // Show stream-style columns if viewing Twitch OR YouTube streams specifically
                const isTw = expandedPlatform === 'twitch' || (expandedPlatform === 'youtube' && panelContentFilter === 'streams');

                if (items.length === 0) return <p className="text-white text-sm text-center py-10">No content in the last {statsTimeRange} days</p>;

                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: 'white', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Content</th>
                        {isTw ? (
                          <>
                            <th style={{ padding: '8px 8px', textAlign: 'right', color: 'white', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Avg CCV</th>
                            <th style={{ padding: '8px 8px', textAlign: 'right', color: 'white', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Peak</th>
                            <th style={{ padding: '8px 8px', textAlign: 'right', color: 'white', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Watch Hrs</th>
                            <th style={{ padding: '8px 8px', textAlign: 'right', color: 'white', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Impressions</th>
                          </>
                        ) : (
                          <>
                            <th style={{ padding: '8px 8px', textAlign: 'right', color: 'white', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Views</th>
                            <th style={{ padding: '8px 8px', textAlign: 'right', color: 'white', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Impr.</th>
                            <th style={{ padding: '8px 8px', textAlign: 'right', color: 'white', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Likes</th>
                            <th style={{ padding: '8px 8px', textAlign: 'right', color: 'white', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Comments</th>
                            <th style={{ padding: '8px 8px', textAlign: 'right', color: 'white', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Eng%</th>
                          </>
                        )}
                        <th style={{ width: 28 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, i) => {
                        const hasSegs = item.segments?.length > 0;
                        const isOpen = panelExpandedRows.has(item.id);
                        const colCount = isTw ? 6 : 7;
                        const gameName = hasSegs
                          ? (item.segments.length === 1 ? item.segments[0].game?.title : `${item.segments.length} games`)
                          : (item.game?.title || null);

                        return (
                          <React.Fragment key={item.id || i}>
                            <tr
                              style={{ borderBottom: isOpen ? 'none' : '1px solid rgba(255,255,255,0.05)', cursor: hasSegs ? 'pointer' : 'default' }}
                              onClick={() => { if (!hasSegs) return; const next = new Set(panelExpandedRows); if (next.has(item.id)) next.delete(item.id); else next.add(item.id); setPanelExpandedRows(next); }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <td style={{ padding: '10px 12px', maxWidth: 200 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {/* Game cover thumbnail */}
                                  {(() => {
                                    const coverUrl = item.game?.coverImageUrl || (hasSegs && item.segments[0]?.game?.coverImageUrl);
                                    return coverUrl ? (
                                      <img src={coverUrl} alt="" style={{ width: 28, height: 38, objectFit: 'cover', borderRadius: 4, flexShrink: 0, border: '1px solid rgba(255,255,255,0.1)' }} />
                                    ) : null;
                                  })()}
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      {hasSegs && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 9, transition: 'transform 0.15s', transform: isOpen ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>▾</span>}
                                      <p style={{ color: 'white', fontWeight: 500, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{item.title || 'Untitled'}</p>
                                    </div>
                                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, margin: '2px 0 0', paddingLeft: hasSegs ? 14 : 0 }}>
                                      {item.postDate ? new Date(item.postDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                                      {item.streamDuration > 0 && ` · ${formatDuration(item.streamDuration)}`}
                                      {gameName && <span style={{ color: 'rgba(236,72,153,0.8)' }}> · {gameName}</span>}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              {isTw ? (
                                <>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{(item.streamAvgCcv || 0).toLocaleString()}</td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{(item.streamPeakCcv || 0).toLocaleString()}</td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{(item.streamWatchHours || 0).toLocaleString()}</td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{(item.impressions || 0).toLocaleString()}</td>
                                </>
                              ) : (
                                <>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{(item.views || item.impressions || 0).toLocaleString()}</td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{(item.impressions || 0).toLocaleString()}</td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{(item.likes || 0).toLocaleString()}</td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{(item.comments || 0).toLocaleString()}</td>
                                  <td style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{item.engagementRate ? `${item.engagementRate.toFixed(1)}%` : '-'}</td>
                                </>
                              )}
                              <td style={{ padding: '10px 8px' }}>
                                {item.contentUrl && (
                                  <a href={item.contentUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.4)' }} onClick={e => e.stopPropagation()}>
                                    <ExternalLink style={{ width: 12, height: 12 }} />
                                  </a>
                                )}
                              </td>
                            </tr>
                            {hasSegs && isOpen && (
                              <tr>
                                <td colSpan={colCount} style={{ padding: 0, borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)' }}>
                                  <div style={{ padding: '6px 12px 10px', borderLeft: `2px solid ${PLATFORM_COLORS[expandedPlatform]}40`, marginLeft: 12 }}>
                                    {item.segments.map((seg, si) => (
                                      <div key={si} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: si < item.segments.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                                        {seg.game?.coverImageUrl ? (
                                          <img src={seg.game.coverImageUrl} alt="" style={{ width: 22, height: 30, objectFit: 'cover', borderRadius: 3, flexShrink: 0, border: '1px solid rgba(255,255,255,0.1)' }} />
                                        ) : (
                                          <div style={{ width: 22, height: 30, borderRadius: 3, background: 'rgba(255,255,255,0.06)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🎮</div>
                                        )}
                                        <span style={{ color: 'white', fontSize: 11, fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seg.game?.title || 'Unknown'}</span>
                                        <div style={{ display: 'flex', gap: 12, fontSize: 10, color: 'white', flexShrink: 0 }}>
                                          {seg.durationMinutes > 0 && <span>{formatDuration(seg.durationMinutes)}</span>}
                                          {seg.avgCcv > 0 && <span>CCV {seg.avgCcv.toLocaleString()}</span>}
                                          {seg.peakCcv > 0 && <span>Peak {seg.peakCcv.toLocaleString()}</span>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>

            {/* Footer */}
            {expandedPlatform === 'youtube' && (
              <div className="px-4 py-2 border-t border-white/10">
                <p className="text-[10px] text-white">YouTube data via YouTube Data API. Refreshed every 30 days.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CreatorDetail;